// Shared registration form pages: name and details, then terms, then one
// page per survey question.
//
// Used by the public page after the mailbox and by the onsite page after
// attract. Substeps live inside the public `form` stage so flow.ts does not
// grow a start → form row. Survey questions sit after terms; a stand that
// published none skips them. Skip on a question leaves it unanswered.

import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

import { FieldSet } from "../../ui/src/fields/FieldSet.js";
import { composeDisplayName, foldNameAnswers } from "../../ui/src/fields/name.js";
import { validateFieldSet } from "../../ui/src/fields/validate.js";
import type { FieldDefinition, FieldValue, FieldValues } from "../../ui/src/fields/types.js";
import { DocumentModal } from "../../ui/src/legal/DocumentView.js";
import { emphasizeTickText } from "../../ui/src/legal/tickMarkup.js";
import type { AgreementDocument } from "../../ui/src/legal/documents.js";
import type { ConsentGrant } from "./flow.js";
import type { AttestationDocument, ConsentDocument } from "./privacy/notices.js";

export type FormPage = "details" | "terms" | "survey";

export type SubmittedDetails = {
  displayName: string;
  phone: string;
  fields: Readonly<Record<string, unknown>>;
  consent: readonly ConsentGrant[];
};

export function isSurveyField(field: FieldDefinition): boolean {
  return field.type === "survey_question";
}

export function splitRegistrationFields(fields: readonly FieldDefinition[]): {
  details: FieldDefinition[];
  survey: FieldDefinition[];
} {
  const details: FieldDefinition[] = [];
  const survey: FieldDefinition[] = [];
  for (const field of fields) {
    if (isSurveyField(field)) survey.push(field);
    else details.push(field);
  }
  return { details, survey };
}

/** Email and code on the public page, then details, terms, and one page per survey question. */
export function registrationPageCount(
  fields: readonly FieldDefinition[],
  leadIn: number,
): number {
  return leadIn + 2 + fields.filter(isSurveyField).length;
}

export function BackLink({
  onClick,
  disabled,
  children = "Back",
  "aria-label": ariaLabel,
}: {
  onClick: () => void;
  disabled?: boolean | undefined;
  children?: string;
  "aria-label"?: string | undefined;
}): ReactNode {
  return (
    <button
      type="button"
      className="web-back"
      onClick={onClick}
      disabled={disabled === true}
      aria-label={ariaLabel}
    >
      <span aria-hidden="true">&larr;</span> {children}
    </button>
  );
}

/**
 * Answers the register route will accept.
 *
 * Empty and null values are omitted rather than sent: an unanswered optional
 * field is the absence of a key, and sending `""` would look like somebody
 * typed nothing on purpose. On the public page `contact_email` is never sent —
 * the ticket is the proof, and a second address here would be the one nobody
 * proved. Onsite keeps it because presence is the proof and the mailbox was
 * typed on this screen.
 */
export function answersForRegister(
  values: FieldValues,
  keepMailbox: boolean,
  foldDisplayName = false,
): Record<string, unknown> {
  const source = foldDisplayName ? foldNameAnswers(values) : values;
  const out: Record<string, unknown> = {};
  for (const [id, value] of Object.entries(source)) {
    if (id === "contact_email" && !keepMailbox) continue;
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[id] = value;
  }
  return out;
}

export function Progress({ step, of }: { step: number; of: number }): ReactNode {
  return (
    <div className="web-progress" aria-live="polite">
      <div className="web-progress-meter" aria-hidden="true">
        {Array.from({ length: of }, (_, index) => (
          <i key={index} data-on={index < step ? "true" : "false"} />
        ))}
      </div>
      <p>Step {step} of {of}</p>
    </div>
  );
}

export function RegistrationBottom({
  step,
  of,
  onBack,
  disabled,
}: {
  step: number;
  of: number;
  onBack?: (() => void) | undefined;
  disabled?: boolean | undefined;
}): ReactNode {
  return (
    <nav className="web-reg-bottom" aria-label="Registration progress">
      <div className="web-reg-bottom-back">
        {onBack ? <BackLink onClick={onBack} disabled={disabled} /> : null}
      </div>
      <Progress step={step} of={of} />
      <div className="web-reg-bottom-end" aria-hidden="true" />
    </nav>
  );
}

/**
 * Whether a document popup on this page may offer the public URL.
 *
 * A context rather than a prop because every tick, legend and lede on the terms
 * page opens documents, and threading the same answer through seven call sites
 * is how six of them keep it and one forgets. The provider is on
 * RegistrationSteps, which is the only thing that knows which page this is.
 */
const PublicDocumentLink = createContext(true);

export function DocumentOpener({
  document,
  children,
}: {
  document: AgreementDocument | undefined;
  children: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const offerPublicLink = useContext(PublicDocumentLink);
  if (document === undefined) return children;
  return (
    <>
      <button type="button" className="web-doclink" onClick={() => setOpen(true)}>
        {children}
      </button>
      {open ? (
        <DocumentModal
          document={document}
          onClose={() => setOpen(false)}
          publicLink={offerPublicLink ? "show" : "hide"}
        />
      ) : null}
    </>
  );
}

const MARKDOWN_LINK = /\[([^\]]+)\]\((https:[^)]+)\)/g;

function TickCopy({
  body,
  documents,
}: {
  body: string;
  documents: readonly AgreementDocument[];
}): ReactNode {
  const parts: ReactNode[] = [];
  let last = 0;
  let index = 0;
  for (const match of body.matchAll(MARKDOWN_LINK)) {
    const at = match.index ?? 0;
    if (at > last) parts.push(emphasizeTickText(body.slice(last, at)));
    const href = match[2] ?? "";
    const label = match[1] ?? href;
    const document = documents.find((row) => row.href === href);
    parts.push(
      document !== undefined ? (
        <DocumentOpener key={`${href}-${index}`} document={document}>
          {document.title}
        </DocumentOpener>
      ) : (
        <a key={`${href}-${index}`} href={href} target="_blank" rel="noreferrer">
          {label}
        </a>
      ),
    );
    last = at + match[0].length;
    index += 1;
  }
  if (last < body.length) parts.push(emphasizeTickText(body.slice(last)));
  return parts.length === 1 ? parts[0] : parts;
}

function Dock({
  why,
  children,
}: {
  why?: string | undefined;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <div className="web-dock-space" aria-hidden="true" />
      <div className="web-dock">
        <div className="web-dock-fade" aria-hidden="true" />
        <div className="web-dock-inner">
          <div className="web-dock-bar">
            <p
              className="web-dock-why"
              id="web-dock-why"
              aria-live="polite"
              data-visible={why === undefined ? "false" : "true"}
            >
              {why ?? "\u00a0"}
            </p>
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

const COUNT_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight"];

/**
 * The sentence under the ticks, counted from the lines drawn (the hand-off's
 * termsNote), so a stand with one optional line never reads "the other two".
 */
export function termsNote(required: number, optional: number, action: string): string {
  const req = required === 1 ? "A starred line is required." : "The starred lines are required.";
  if (optional === 0) return req;
  const other = optional === 1 ? "The other one is not" : `The other ${COUNT_WORDS[optional] ?? optional} are not`;
  return `${req} ${other}, and ${action} does not wait for ${optional === 1 ? "it" : "them"}.`;
}

function Tick({
  id,
  checked,
  onChange,
  lang,
  heading,
  required = false,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  lang?: string | undefined;
  heading?: string | undefined;
  /** A line Submit waits for: starred, as the note under the ticks says. */
  required?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      {heading !== undefined && heading !== "" ? <p className="web-tick-heading">{heading}</p> : null}
      <label className="web-tick" htmlFor={id} lang={lang}>
        <input
          id={id}
          name={id}
          type="checkbox"
          checked={checked}
          aria-required={required ? true : undefined}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="web-tick-copy">
          {children}
          {required ? (
            <span className="web-req" aria-hidden="true">
              {" "}*
            </span>
          ) : null}
        </span>
      </label>
    </>
  );
}

export type RegistrationStepsProps = {
  lede: ReactNode;
  busy: boolean;
  consent: readonly ConsentDocument[];
  attestations?: readonly AttestationDocument[];
  privacyNotice?: string | null;
  fields: readonly FieldDefinition[];
  legal: readonly AgreementDocument[];
  keepMailbox: boolean;
  foldDisplayName?: boolean;
  onSubmit: (details: SubmittedDetails) => void;
  step: FormPage;
  onStep: (next: FormPage) => void;
  onLeaveDetails?: (() => void) | undefined;
  leadIn?: number;
  /** A surface we own, where a document popup must not offer a way out of the app. */
  onsite?: boolean;
};

export function RegistrationSteps(props: RegistrationStepsProps): ReactNode {
  return (
    <PublicDocumentLink.Provider value={props.onsite !== true}>
      <RegistrationStepPage {...props} />
    </PublicDocumentLink.Provider>
  );
}

function RegistrationStepPage({
  lede,
  busy,
  consent,
  attestations = [],
  privacyNotice = null,
  fields,
  legal,
  keepMailbox,
  foldDisplayName = false,
  onSubmit,
  step,
  onStep,
  onLeaveDetails,
  leadIn = 0,
}: RegistrationStepsProps): ReactNode {
  const [values, setValues] = useState<FieldValues>({});
  const [tried, setTried] = useState(false);
  const [agreed, setAgreed] = useState<Record<string, boolean>>({});
  const [legacyTerms, setLegacyTerms] = useState(false);
  const [requiredOk, setRequiredOk] = useState<Record<string, boolean>>({});
  const acceptedTerms =
    attestations.length === 0
      ? legacyTerms
      : attestations.every((row) => requiredOk[row.attestation_version_id] === true);
  const [surveyIndex, setSurveyIndex] = useState(0);
  const [skipped, setSkipped] = useState<ReadonlySet<string>>(() => new Set());
  const { details: detailFields, survey: surveyFields } = splitRegistrationFields(fields);
  const hasSurvey = surveyFields.length > 0;
  const currentSurvey = surveyFields[surveyIndex];
  const detailVerdict = validateFieldSet(detailFields, values, "attendee");
  const pendingSurvey = surveyFields.filter((field) => !skipped.has(field.field_id));
  const surveyVerdict = validateFieldSet(pendingSurvey, values, "attendee");
  const detailsOutstanding = detailVerdict.blocking.length > 0;
  const surveyOutstanding = surveyVerdict.blocking.length > 0;
  const fieldsOutstanding = detailsOutstanding || surveyOutstanding;
  // Terms may gate Submit. A consent may not — UK GDPR Article 7(4), enforced
  // in xp/ui/src/agreement.ts. `agreed` is deliberately not in this expression.
  const submitBlocked = busy || fieldsOutstanding || !acceptedTerms;
  const of = registrationPageCount(fields, leadIn);

  const change = useCallback((field_id: string, value: FieldValue) => {
    setSkipped((current) => {
      if (!current.has(field_id)) return current;
      const next = new Set(current);
      next.delete(field_id);
      return next;
    });
    setValues((current) => ({ ...current, [field_id]: value }));
  }, []);

  const terms = legal.find((document) => document.kind === "terms");
  const privacy = legal.find((document) => document.kind === "privacy_notice");
  const rules = legal.find((document) => document.kind === "official_rules");

  const fireSubmit = (ignore = skipped) => {
    const pending = surveyFields.filter((field) => !ignore.has(field.field_id));
    const outstanding = validateFieldSet(pending, values, "attendee").blocking.length > 0;
    if (busy || detailsOutstanding || outstanding || !acceptedTerms) {
      setTried(true);
      return;
    }
    const phone = typeof values["phone"] === "string" ? values["phone"].trim() : "";
    onSubmit({
      displayName: composeDisplayName(values) ?? "",
      phone,
      fields: answersForRegister(values, keepMailbox, foldDisplayName),
      consent: consent
        .filter((document) => agreed[document.purpose] === true)
        .map((document) => ({
          purpose: document.purpose,
          consent_version_id: document.consent_version_id,
          wording_hash: document.wording_hash,
        })),
    });
  };

  const goSurvey = (nextIndex: number) => {
    setTried(false);
    if (nextIndex < 0) {
      onStep("terms");
      return;
    }
    if (nextIndex >= surveyFields.length) {
      fireSubmit();
      return;
    }
    setSurveyIndex(nextIndex);
    if (step !== "survey") onStep("survey");
  };

  const skipSurvey = () => {
    if (currentSurvey === undefined) return;
    const nextSkipped = new Set(skipped).add(currentSurvey.field_id);
    setSkipped(nextSkipped);
    setTried(false);
    if (surveyIndex + 1 >= surveyFields.length) {
      fireSubmit(nextSkipped);
      return;
    }
    setSurveyIndex(surveyIndex + 1);
  };

  if (step === "details") {
    return (
      <section className="web-form web-form--details">
        <h1>Enter Your Details</h1>
        {lede}
        <form
          className="web-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (detailsOutstanding) {
              setTried(true);
              return;
            }
            setTried(false);
            onStep("terms");
          }}
        >
          <FieldSet
            fields={detailFields}
            values={values}
            onChange={change}
            audience="attendee"
            problems={detailVerdict.verdicts}
            showSummary={tried}
            summaryPresentation="fields"
            disabled={busy}
            labelsAsPlaceholders
          />
          <Dock>
            <button type="submit" className="xp-btn xp-btn--primary" disabled={busy} aria-busy={busy}>
              Next
            </button>
          </Dock>
        </form>
        <RegistrationBottom
          step={leadIn + 1}
          of={of}
          onBack={onLeaveDetails}
          disabled={busy}
        />
      </section>
    );
  }

  if (step === "survey" && currentSurvey !== undefined) {
    const currentVerdict = validateFieldSet([currentSurvey], values, "attendee");
    const last = surveyIndex + 1 >= surveyFields.length;
    return (
      <section className="web-form web-form--survey">
        <h1>Question No. {surveyIndex + 1}</h1>
        <form
          className="web-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (currentVerdict.blocking.length > 0) {
              setTried(true);
              return;
            }
            if (last) {
              fireSubmit();
              return;
            }
            goSurvey(surveyIndex + 1);
          }}
        >
          <FieldSet
            fields={[currentSurvey]}
            values={values}
            onChange={change}
            audience="attendee"
            problems={currentVerdict.verdicts}
            showSummary={tried}
            disabled={busy}
          />
          <Dock>
            <div className="xp-acts web-survey-acts">
              <button
                type="submit"
                className="xp-btn xp-btn--primary"
                disabled={busy}
                aria-busy={busy}
              >
                {last ? <>{busy ? "Registering…" : "Submit"}</> : "Next"}
              </button>
              <button type="button" className="xp-btn web-btn-quiet" disabled={busy} onClick={skipSurvey}>
                Skip
              </button>
            </div>
          </Dock>
        </form>
        <RegistrationBottom
          step={leadIn + 3 + surveyIndex}
          of={of}
          onBack={() => goSurvey(surveyIndex - 1)}
          disabled={busy}
        />
      </section>
    );
  }

  return (
    <section className="web-form web-form--terms">
      <h1>Terms</h1>
      <form
        className="web-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!acceptedTerms) {
            setTried(true);
            return;
          }
          if (hasSurvey) {
            setSurveyIndex(0);
            setTried(false);
            onStep("survey");
            return;
          }
          if (submitBlocked) {
            setTried(true);
            return;
          }
          fireSubmit();
        }}
      >
        {attestations.map((row) => (
          <Tick
            key={row.attestation_version_id}
            id={row.attestation_version_id}
            checked={requiredOk[row.attestation_version_id] === true}
            lang={row.locale}
            heading={row.heading}
            required
            onChange={(checked) =>
              setRequiredOk((current) => ({ ...current, [row.attestation_version_id]: checked }))
            }
          >
            <TickCopy body={row.body} documents={legal} />
          </Tick>
        ))}

        {attestations.length === 0 ? (
          <Tick id="accept-terms" checked={legacyTerms} onChange={setLegacyTerms} required>
            I have read and agree to the <DocumentOpener document={terms}>Terms</DocumentOpener>.
          </Tick>
        ) : null}

        {consent.length > 0 ? (
          <fieldset>
            {/* Said to a screen reader as the group's name; the counted note
                under the ticks says the same thing to everybody else. */}
            <legend className="sr">
              None of these is required. {hasSurvey ? "Next" : "Submit"} does not wait for them.
            </legend>
            {consent.map((document) => (
              <Tick
                key={document.purpose}
                id={document.purpose}
                checked={agreed[document.purpose] === true}
                lang={document.locale}
                heading={document.heading}
                onChange={(checked) =>
                  setAgreed((current) => ({ ...current, [document.purpose]: checked }))
                }
              >
                <TickCopy body={document.body} documents={legal} />
              </Tick>
            ))}
          </fieldset>
        ) : null}

        {/* The hand-off's note under the ticks: counted from the lines actually
            drawn, then the operator's notice and the documents. */}
        <p className="web-controller">
          {termsNote(Math.max(attestations.length, 1), consent.length, hasSurvey ? "Next" : "Submit")}
          {privacyNotice !== null && privacyNotice !== "" ? <> {privacyNotice}</> : null}
          {privacy !== undefined ? (
            <>
              {" "}
              <DocumentOpener document={privacy}>Privacy</DocumentOpener>
            </>
          ) : null}
          {rules !== undefined ? (
            <>
              {" "}
              <DocumentOpener document={rules}>{rules.title}</DocumentOpener>
            </>
          ) : null}
        </p>

        <Dock why={!acceptedTerms ? "Tick the required boxes to continue." : undefined}>
          <button
            type="submit"
            className="xp-btn xp-btn--primary"
            disabled={hasSurvey ? busy || !acceptedTerms : submitBlocked}
            aria-busy={busy}
            aria-describedby={!acceptedTerms ? "web-dock-why" : undefined}
          >
            {hasSurvey ? "Next" : busy ? "Registering…" : "Submit"}
          </button>
        </Dock>
      </form>
      <RegistrationBottom
        step={leadIn + 2}
        of={of}
        onBack={() => {
          setTried(false);
          onStep("details");
        }}
        disabled={busy}
      />
    </section>
  );
}

export function legalDocumentsForPage(
  published: readonly AgreementDocument[],
  drafts: readonly AgreementDocument[],
): AgreementDocument[] {
  const cookie = drafts.find((document) => document.document_id === "cookie_notice");
  const chosen =
    published.length > 0
      ? [...published]
      : drafts.filter((document) => document.document_id !== "cookie_notice");
  if (cookie !== undefined && !chosen.some((document) => document.document_id === "cookie_notice")) {
    chosen.push(cookie);
  }
  return chosen;
}
